<template>
    <transition name="cookieNotice">
        <div v-if="visible"
             class="cookieNotice"
             :class="{ raised }"
             role="dialog"
             aria-label="שימוש בקוקיז">

            <!-- an inline icon, not font awesome: the landing page loads the icon font only when the
                 notice first needs it, and the button showed up empty -->
            <button type="button"
                    class="cookieNoticeClose"
                    aria-label="סגירה"
                    @click="dismiss">
                <svg viewBox="0 0 24 24"
                     aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" />
                </svg>
            </button>

            <div class="cookieNoticeTitle">
                האתר הזה משתמש ב-Cookies
            </div>

            <div class="cookieNoticeText">
                <p>
                    אנחנו משתמשים בהם כדי שהמערכת תעבוד כמו שצריך ולשיפור חוויית הגלישה.
                </p>
                <p>
                    המשך השימוש באתר מהווה הסכמה
                    ל<router-link to="/terms">תנאי השימוש</router-link>
                    ול<router-link to="/privacy">מדיניות הפרטיות</router-link>.
                </p>
            </div>

            <!-- a phone: this one line instead of the card (the media query below) -->
            <p class="cookieNoticeLine">
                האתר משתמש ב-Cookies ·
                <router-link to="/terms">תנאי שימוש</router-link>
                ·
                <router-link to="/privacy">פרטיות</router-link>
            </p>
        </div>
    </transition>
</template>

<script>
//closing the notice is remembered per browser (the date it was closed) - the landing page opened on its
//own (public/landing.html, planup.co.il/ for a visitor) shows the same notice and reads / writes the same key
const STORAGE_KEY = 'cookieNoticeClosed';

//planup's public site - the only pages the notice shows on. Never inside the logged-in system, and
//not on the contract-sign page either (it opens without a login, but a business's customer signs
//there - not a visitor of planup's). Not on the signup wizard: on a phone it sat on the wizard's
//button (the signup review, 2/11/2026)
const SHOWN_ON_ROUTES = [
    'landing', 'Blog', 'BlogPost', 'Terms', 'Privacy', 'Accessibility',
    'login', 'passwordRecovery', 'passwordRecoveryWithToken'
];

function readClosed() {
    try {
        return !!localStorage.getItem(STORAGE_KEY);
    } catch (e) {
        return false;
    }
}

function insideFrame() {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
}

export default {
    name: 'CookieNotice',
    data() {
        return {
            closed: readClosed(),
            //shown a moment after the first route resolves - no flash on a page it skips
            ready: false
        };
    },
    computed: {
        visible() {
            if (this.closed || !this.ready || insideFrame()) {
                return false;
            }
            //a logged-in user reading the terms or the privacy policy is inside the system (its side
            //menu is around the page) - no notice there either
            return SHOWN_ON_ROUTES.includes(this.$route.name) && !this.$store.state.isLogged;
        },
        //on a phone the notice is a line along the bottom edge, where the landing page's bottom bar lives
        //(up to 760px) - it goes above it (see the media query)
        raised() {
            return this.$route.name === 'landing';
        }
    },
    mounted() {
        if (this.closed) {
            return;
        }
        this.$router.onReady(() => {
            this.readyTimer = setTimeout(() => {
                this.ready = true;
            }, 800);
        });
    },
    beforeDestroy() {
        clearTimeout(this.readyTimer);
    },
    methods: {
        dismiss() {
            this.closed = true;
            try {
                localStorage.setItem(STORAGE_KEY, new Date().toISOString());
            } catch (e) {
                //storage blocked (private mode and such) - closed for this visit only
            }
        }
    }
};
</script>

<style scoped>
/* planup's palette (the landing page's): purple #7351c8 / #5f40b1, lavender #f6f2fe / #ece5fb,
   text #474d66, line #e7e3f1 - and the logo's arch along the top edge */
.cookieNotice {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1040;
    width: 380px;
    max-width: calc(100vw - 32px);
    padding: 26px 26px 14px;
    background: #fff;
    border: 1px solid #e7e3f1;
    border-radius: 16px;
    box-shadow: 0 18px 40px -12px rgba(115, 81, 200, .32), 0 2px 8px rgba(28, 32, 51, .06);
    overflow: hidden;
    direction: rtl;
    text-align: center;
    color: #474d66;
    font-size: 14.5px;
    line-height: 1.6;
}

/* the logo's arch, segment by segment: lavender, pink, blue, yellow, pink, lavender */
.cookieNotice::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 4px;
    background: linear-gradient(to right,
        #a58ae3 0%, #a58ae3 6%, #df4677 6%, #df4677 25%, #3a9fd6 25%, #3a9fd6 50%,
        #efb534 50%, #efb534 75%, #df4677 75%, #df4677 94%, #a58ae3 94%, #a58ae3 100%);
}

.cookieNoticeClose {
    position: absolute;
    top: 16px;
    left: 14px;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #f6f2fe;
    color: #7351c8;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

/* sized here - the app has a global svg { width: 100% } rule */
.cookieNoticeClose svg {
    width: 13px;
    height: 13px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.4;
    stroke-linecap: round;
}

.cookieNoticeClose:hover {
    background: #ece5fb;
    color: #5f40b1;
}

.cookieNoticeTitle {
    margin: 2px 34px 8px;
    font-size: 19px;
    font-weight: 700;
    color: #5f40b1;
}

.cookieNotice p {
    margin: 0 0 8px;
}

.cookieNotice a {
    color: #7351c8;
    font-weight: 600;
    text-decoration: underline;
    text-decoration-color: rgba(115, 81, 200, .35);
    text-underline-offset: 3px;
}

.cookieNotice a:hover {
    color: #5f40b1;
    text-decoration-color: currentColor;
}

.cookieNotice .cookieNoticeLine {
    display: none;
}

/* a phone: one line along the bottom (text, the two links, ×) - the card was ~180px tall there and
   covered the page's own buttons */
@media (max-width: 760px) {
    .cookieNotice {
        left: 12px;
        right: 12px;
        bottom: calc(12px + env(safe-area-inset-bottom));
        width: auto;
        max-width: none;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 7px 12px 7px 7px;
        border-radius: 12px;
        text-align: right;
        font-size: 13px;
        line-height: 1.45;
    }

    .cookieNotice::before {
        height: 3px;
    }

    .cookieNoticeTitle,
    .cookieNoticeText {
        display: none;
    }

    .cookieNotice .cookieNoticeLine {
        display: block;
        flex: 1;
        min-width: 0;
        margin: 0;
    }

    .cookieNotice a {
        text-underline-offset: 2px;
    }

    .cookieNoticeClose {
        position: static;
        order: 1;
        flex-shrink: 0;
        width: 28px;
        height: 28px;
    }

    .cookieNotice.raised {
        bottom: calc(90px + env(safe-area-inset-bottom));
    }
}

@media (max-width: 370px) {
    .cookieNotice {
        font-size: 12.5px;
    }
}

.cookieNotice-enter-active,
.cookieNotice-leave-active {
    transition: opacity .3s ease, transform .3s ease;
}

.cookieNotice-enter,
.cookieNotice-leave-to {
    opacity: 0;
    transform: translateY(16px);
}
</style>
